import { createSlice, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { setCurrentUser } from "./currentuserslice";
import { notificationDurationMiddle } from "../../lib/utils";
import {
  canCreateSchedule,
  canEditSchedule,
  canDeleteSchedule,
  canViewAnySchedule,
} from "../../routing/authorizationhandlers";

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  filter: "",
  showSystemEntities: true,
  exports: [],
  imports: [],
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  panes: [
    {
      // size: "30%",
      size: "25%",
      min: "25%",
      max: "25%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "75%",
      max: "75%",
      collapsible: false,
    },
  ],
};

const scheduleLocatorPageSlice = createSlice({
  name: "scheduleLocatorPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    pageLoaded: (state, action) => {
      //payload: {reports: , currentUser: ,... }
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        exports: (action.payload.exports ?? []).map((item) => ({
          ...item,
          importexport: "export",
        })),
        imports: (action.payload.imports ?? []).map((item) => ({
          ...item,
          importexport: "import",
        })),
        canView: canViewAnySchedule(action.payload.currentUser),
        canCreate: canCreateSchedule(action.payload.currentUser),
        canEdit: canEditSchedule(action.payload.currentUser),
        canDelete: canDeleteSchedule(action.payload.currentUser),
        pageDisposed: false,
      };
    },

    entityDeleted: (state, action) => {
      //payload: the entity id
      let pageState = current(state);
      debugger;
      if (pageState.pageDisposed) return state;
      let id = action.payload.id;
      let type = action.payload.type;
      let exports = pageState.exports;
      let imports = pageState.imports;
      if (type == "export")
        exports = pageState.exports.filter((e) => e.Id != id);
      else if (type == "import")
        imports = pageState.imports.filter((e) => e.Id != id);

      return {
        ...state,
        exports: exports,
        imports: imports,
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default scheduleLocatorPageSlice.reducer;

export const loadPage = (currentUser, t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "scheduleexport/getscheduleexportimportlist",
      method: "post",
      data: {},
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleLocatorPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;
    //transform the response
    dispatch(
      pageLoaded({
        exports: data.Exports,
        imports: data.Imports,
        currentUser: currentUser,
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const deleteEntity = (id, type, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.deletingDataset"),
    }),
  );

  const url =
    type === "export"
      ? "scheduleexport/deletescheduleexport"
      : type === "import"
        ? "scheduleimport/deletescheduleimport"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: { Id: id },
      //headers:{'h':'1'}
    });

    dispatch(setProcessing({ processing: false }));

    dispatch(entityDeleted({ id: id, type: type }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const importDatasetId = (id, t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "dataset/importdatafromdbconnectionperdataset",
      method: "post",
      data: {
        DatasetId: id,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleLocatorPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;
    if (data.ProccessStarted) {
      dispatch(
        addNotification({
          text: t("slices.startedImport"),
          type: "success",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        }),
      );
    }
    dispatch(setProcessing({ processing: false }));
    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const pauseSchedule = (id, type, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  const url =
    type === "export"
      ? "scheduleexport/pausescheduleexportjob"
      : type === "import"
        ? "scheduleimport/pausescheduleimportjob"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: { Id: id },
      //headers:{'h':'1'}
    });

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const resumeSchedule = (id, type, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );
  const url =
    type === "export"
      ? "scheduleexport/resumescheduleexportjob"
      : type === "import"
        ? "scheduleimport/resumescheduleimportjob"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: { Id: id },
    });

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  setPanes,
  pageLoaded,
  entityDeleted,
  disposePage,
} = scheduleLocatorPageSlice.actions;
